import {
  PickersTextField as MuiPickersTextField,
  pickersInputBaseClasses,
  pickersOutlinedInputClasses,
} from '@mui/x-date-pickers-pro'
import { formHelperTextClasses, iconButtonClasses, inputLabelClasses } from '@mui/material'
import { styled } from '@mui/material/styles'
import { youShallNotPass } from 'lib/helpers/youShallNotPass/youShallNotPass'
import * as tkns from '../../tokens/common/Input'
import * as outlinedTkns from '../../tokens/common/InputOutlined'

export interface PickersTextFieldStyledProps {
  warning?: boolean
}

// `pickersInputBaseClasses` declares `sectionsContainer` and `readOnly` in its types but omits both
// from the runtime classes object (v9.4.0), so the constants are `undefined` even though they
// type-check. `readOnly` is a global state slot, so its class is un-namespaced.
const sectionsContainerClass = 'MuiPickersInputBase-sectionsContainer'
const readOnlyClass = 'Mui-readOnly'

/**
 * Design-system styling for the MUI X picker `textField` slot. The section-based field can't be a
 * plain `MuiTextField`, so the `TextField` look is re-applied here onto the picker classes
 * (`PickersInputBase` / `PickersOutlinedInput`); the label still uses `MuiInputLabel`.
 */
export const PickersTextField = styled(MuiPickersTextField, {
  shouldForwardProp: youShallNotPass('warning'),
})<PickersTextFieldStyledProps>(({ theme, warning, error, size }) => ({
  // The section list only inherits `theme.typography.body1` (16px); re-apply the input text scale
  // so dates render at the design-system 14px, not a size larger.
  [`& .${pickersInputBaseClasses.root}`]: {
    fontSize: tkns.InputBaseTypographyTextFontSize,
    lineHeight: tkns.InputBaseTypographyTextLineHeight,
    letterSpacing: tkns.InputBaseTypographyTextLetterSpacing,
  },
  [`& .${sectionsContainerClass}`]: {
    boxSizing: 'border-box',
    // A flex row whose minHeight exceeds the line box won't vertically center the value on its own.
    alignItems: 'center',
    // MUI hard-codes `width: 182px` here; let it flex with the container instead (`flex-grow: 1` is
    // inherited) so two date fields in a narrow column don't overflow.
    width: 'auto',
    minWidth: 0,
    ...(size === 'small' && {
      padding: '4px 0',
      minHeight: tkns.InputBaseSizeSmallMinHeight,
    }),
    ...(size === 'medium' && {
      padding: '8px 0',
      minHeight: tkns.InputBaseSizeMediumMinHeight,
    }),
  },
  [`& .${pickersOutlinedInputClasses.root}`]: {
    // Outlined root is transparent by default; the surface is white in every state but read-only,
    // which sets its own below. The left inset lives here (not on the sections) so the
    // floating-label notch lines up; the root's default 14px right padding is the icon gap.
    backgroundColor: outlinedTkns.InputOutlinedColorDefaultBackground,
    ...(size === 'small' && {
      paddingLeft: '8px',
    }),
    ...(size === 'medium' && {
      paddingLeft: '12px',
    }),
  },
  // While the label rests inside (empty + unfocused) it reads as the placeholder, but the field
  // also paints the MM/DD/YYYY mask in that spot — hide the mask while the label rests so the two
  // don't overlap.
  [`&:has(.${inputLabelClasses.root}:not(.${inputLabelClasses.shrink})) .${sectionsContainerClass}`]: {
    color: 'transparent',
  },
  [`& .${inputLabelClasses.root}`]: {
    ...(size === 'small' && {
      transform: 'translate(8px, 6px) scale(1)',
    }),
    ...(size === 'medium' && {
      transform: 'translate(12px, 10px) scale(1)',
    }),
  },
  [`& .${inputLabelClasses.root}.${inputLabelClasses.shrink}`]: {
    ...(size === 'small' && {
      transform: 'translate(8px, -9px) scale(0.75)',
    }),
    ...(size === 'medium' && {
      transform: 'translate(12px, -9px) scale(0.75)',
    }),
  },
  // A resting label doubles as the placeholder, so cap its width to clear the calendar button —
  // otherwise a long label bleeds under the icon instead of truncating. Resting label only; a
  // floated one sits clear above the field.
  [`& .${inputLabelClasses.root}:not(.${inputLabelClasses.shrink})`]: {
    ...(size === 'small' && {
      maxWidth: 'calc(100% - 54px)',
    }),
    ...(size === 'medium' && {
      maxWidth: 'calc(100% - 58px)',
    }),
  },
  // PickersOutlinedInput doesn't inherit the `MuiOutlinedInput` theme override, so its idle and
  // disabled borders are re-applied here from the same tokens plain text fields use.
  [`& .${pickersOutlinedInputClasses.notchedOutline}`]: {
    borderColor: outlinedTkns.InputOutlinedColorDefaultOutline,
    borderWidth: outlinedTkns.InputOutlinedBorderWidthDefault,
    ...(size === 'small' && {
      paddingLeft: '2px',
    }),
    ...(size === 'medium' && {
      paddingLeft: '6px',
    }),
  },
  [`& .${pickersOutlinedInputClasses.root}.${readOnlyClass}`]: {
    backgroundColor: outlinedTkns.InputOutlinedColorReadOnlyBackground,
    cursor: 'default',

    // Read-only holds MUI's `focused` flag down so the label never floats, so the focused class
    // never lands here and the focus styling keys off the DOM instead. Scoped to the sections so a
    // focused adornment button doesn't paint the whole field as focused.
    [`&:has(.${sectionsContainerClass}:focus-within)`]: {
      backgroundColor: outlinedTkns.InputOutlinedColorReadOnlyFocusedBackground,
    },

    [`& .${pickersOutlinedInputClasses.notchedOutline}`]: {
      borderColor: outlinedTkns.InputOutlinedColorReadOnlyOutline,
      borderWidth: outlinedTkns.InputOutlinedBorderWidthReadOnly,
    },
    // Read-only stays tabbable, so it still needs a focus affordance — a neutral one, since the
    // brand-colored ring reads as "you can type here".
    [`&:has(.${sectionsContainerClass}:focus-within) .${pickersOutlinedInputClasses.notchedOutline}`]: {
      borderColor: outlinedTkns.InputOutlinedColorReadOnlyFocusedOutline,
      borderWidth: outlinedTkns.InputOutlinedBorderWidthReadOnlyFocused,
    },
    // Read-only renders both adornment buttons disabled, and a disabled IconButton's own color beats
    // anything inherited from the adornment.
    [`& .${iconButtonClasses.root}`]: {
      color: outlinedTkns.InputOutlinedColorReadOnlyTrailingIcon,
    },
  },
  // Read-only stays focusable, so the focused label color has to be undone; error and disabled own
  // the label whenever they're also set.
  [`&:has(.${readOnlyClass}) .${inputLabelClasses.root}:not(.${inputLabelClasses.error}):not(.${inputLabelClasses.disabled})`]:
    {
      color: outlinedTkns.InputOutlinedColorReadOnlyLabel,
    },
  [`& .${pickersOutlinedInputClasses.root}.${pickersOutlinedInputClasses.disabled} .${pickersOutlinedInputClasses.notchedOutline}`]:
    {
      borderColor: outlinedTkns.InputOutlinedColorDisabledOutline,
      borderWidth: outlinedTkns.InputOutlinedBorderWidthDisabled,
    },
  // MUI colors the focused outline from `palette[inputColor].main` and darkens the border on hover;
  // the design system uses fixed tokens for both, re-applied here. Error/warning own their own
  // border (handled below), so they're excluded.
  [`& .${pickersOutlinedInputClasses.root}.${pickersOutlinedInputClasses.focused}:not(.${pickersOutlinedInputClasses.error}):not(.${readOnlyClass}) .${pickersOutlinedInputClasses.notchedOutline}`]:
    {
      borderColor: outlinedTkns.InputOutlinedColorFocusedOutline,
      borderWidth: outlinedTkns.InputOutlinedBorderWidthFocused,
    },
  ...(!warning && {
    [`& .${pickersOutlinedInputClasses.root}:hover:not(.${pickersOutlinedInputClasses.focused}):not(.${pickersOutlinedInputClasses.disabled}):not(.${pickersOutlinedInputClasses.error}):not(.${readOnlyClass}) .${pickersOutlinedInputClasses.notchedOutline}`]:
      {
        borderColor: outlinedTkns.InputOutlinedColorHoveredOutline,
        borderWidth: outlinedTkns.InputOutlinedBorderWidthHovered,
      },
  }),
  ...(error && {
    [`& .${pickersOutlinedInputClasses.root}:not(.${pickersOutlinedInputClasses.focused}):not(.${pickersOutlinedInputClasses.disabled})`]:
      {
        [`& .${pickersOutlinedInputClasses.notchedOutline}, &:hover .${pickersOutlinedInputClasses.notchedOutline}`]: {
          borderColor: theme.palette.error.main,
        },
      },
  }),
  ...(warning &&
    !error && {
      [`& .${pickersOutlinedInputClasses.root}:not(.${pickersOutlinedInputClasses.focused}):not(.${pickersOutlinedInputClasses.disabled})`]:
        {
          [`& .${pickersOutlinedInputClasses.notchedOutline}, &:hover .${pickersOutlinedInputClasses.notchedOutline}`]:
            {
              borderColor: theme.tokens.ThemeColorCommonWarning,
            },
        },
      [`& .${formHelperTextClasses.root}`]: {
        color: theme.tokens.ThemeColorCommonWarning,
      },
      // Element selector, not the `inputLabelClasses.root` key — that key carries the label
      // transform above and the object spread would clobber it.
      '& label': {
        color: theme.tokens.ThemeColorCommonWarning,
      },
    }),
}))
